import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import AnokyeSharingWindow, { ResourceType } from "./AnokyeSharingWindow";

import { GiFullFolder } from "react-icons/gi";
import { BsChatLeftText } from "react-icons/bs";
import { IoDocumentText } from "react-icons/io5";
import { useChatContext } from "@/services/chat-provider";

export const ProjectSharingWindow = forwardRef(
  /**
   * @param {object} props
   * @param {React.ForwardedRef<SharingWindowExternalAPI<object>>} ref
   */
  function ProjectSharingWindow(props, ref) {
    const { useChatProjects, useChatSessionList } = useChatContext()

    const { resource: project, windowRef, handleWindowClose } = useSharingWindowResourceAndRef(ref);
    const { data: projects } = useChatProjects()
    const { data: chatSessions } = useChatSessionList()

    const projectWithDetails = projects?.find((projectItem) => projectItem._id === project);
    const projectChatSessions = chatSessions?.filter(session => session.project === project) || [];
    const chatSessionsInfo = `${projectChatSessions?.length || 0} ${projectChatSessions?.length === 1 ? "Chat" : "Chats"}`;

    return (
      <AnokyeSharingWindow
        resourceType={ResourceType.PROJECT}
        resourceId={project}
        onClose={handleWindowClose}
        ref={windowRef}>
        <AnokyeSharingWindow.Header
          title="Share Project"
          subtitle="Share your project to collaborate with your team"
        />

        <AnokyeSharingWindow.Content>
          <AnokyeSharingWindow.ResourceDetails>
            <AnokyeSharingWindow.ResourceInfo>
              <GiFullFolder />
              <p>{projectWithDetails?.title}</p>
            </AnokyeSharingWindow.ResourceInfo>
            <AnokyeSharingWindow.ResourceStats>{chatSessionsInfo}</AnokyeSharingWindow.ResourceStats>
          </AnokyeSharingWindow.ResourceDetails>

          <AnokyeSharingWindow.LinkSharing />
          <AnokyeSharingWindow.SearchMembers />
          <AnokyeSharingWindow.MembersInfo />
        </AnokyeSharingWindow.Content>

        <AnokyeSharingWindow.Footer />
      </AnokyeSharingWindow>
    );
  }
);

export const ChatSessionSharingWindow = forwardRef(
  /**
   * @param {object} props
   * @param {React.ForwardedRef<SharingWindowExternalAPI<ChatSession>>} ref
   */
  function ChatSessionSharingWindow(props, ref) {
    const { resource: chatSession, windowRef, handleWindowClose } = useSharingWindowResourceAndRef(ref);

    return (
      <AnokyeSharingWindow
        resourceType={ResourceType.CHAT}
        resourceId={chatSession?._id}
        onClose={handleWindowClose}
        ref={windowRef}>
        <AnokyeSharingWindow.Header
          title="Share Chat Session"
          subtitle="Share your chat to collaborate with your team"
        />

        <AnokyeSharingWindow.Content>
          <AnokyeSharingWindow.ResourceDetails>
            <AnokyeSharingWindow.ResourceInfo>
              <BsChatLeftText />
              <p>{chatSession?.title ?? "New Chat"}</p>
            </AnokyeSharingWindow.ResourceInfo>
          </AnokyeSharingWindow.ResourceDetails>

          <AnokyeSharingWindow.LinkSharing />
          <AnokyeSharingWindow.SearchMembers />
          <AnokyeSharingWindow.MembersInfo />
        </AnokyeSharingWindow.Content>

        <AnokyeSharingWindow.Footer />
      </AnokyeSharingWindow>
    );
  }
);

export const DocumentSharingWindow = forwardRef(
  /**
   * @param {object} props
   * @param {React.ForwardedRef<SharingWindowExternalAPI<ChatReport>>} ref
   */
  function DocumentSharingWindow(props, ref) {
    const { resource: document, windowRef, handleWindowClose } = useSharingWindowResourceAndRef(ref);

    const documentName = (() => {
      let report = document;

      if (report == null || Object.values(report).some((item) => !item)) {
        return null;
      }

      const patientName = report?.patientInfo?.name.replace(/\s+/g, "_").toLowerCase();
      const age = report?.patientInfo.age;
      const date = report?.date
        ? new Date(report?.date).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0];

      return `medical_record_${patientName}_${age}_${date}.pdf`;
    })();

    return (
      <AnokyeSharingWindow
        resourceType={ResourceType.REPORT}
        resourceId={document?._id}
        onClose={handleWindowClose}
        ref={windowRef}>
        <AnokyeSharingWindow.Header
          title="Share Document"
          subtitle="Share this document to collaborate with your team"
        />

        <AnokyeSharingWindow.Content>
          <AnokyeSharingWindow.ResourceDetails>
            <AnokyeSharingWindow.ResourceInfo>
              <IoDocumentText />
              <p>{documentName}</p>
            </AnokyeSharingWindow.ResourceInfo>
          </AnokyeSharingWindow.ResourceDetails>

          <AnokyeSharingWindow.LinkSharing />
          <AnokyeSharingWindow.SearchMembers />
          <AnokyeSharingWindow.MembersInfo />
        </AnokyeSharingWindow.Content>

        <AnokyeSharingWindow.Footer />
      </AnokyeSharingWindow>
    );
  }
);

export const ReviewRequestWindow = forwardRef(
  /**
   * @param {object} props
   * @param {React.ForwardedRef<SharingWindowExternalAPI<ChatSession>>} ref
   */
  function ChatSessionSharingWindow(props, ref) {
    const { resource: reportToReview, windowRef, handleWindowClose } = useSharingWindowResourceAndRef(ref);

    const documentName = (() => {
      let report = reportToReview;

      if (report == null) {
        return null;
      }

      const patientName = report?.patient?.name.replace(/\s+/g, "_").toLowerCase();
      const age = report?.patient?.age;
      const date = report?.date
        ? new Date(report?.date).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0];

      return `medical_record_${patientName}_${age}_${date}.pdf`;
    })();

    return (
      <AnokyeSharingWindow
        resourceType={ResourceType.CHAT}
        resourceId={reportToReview?._id}
        onClose={handleWindowClose}
        ref={windowRef}>
        <AnokyeSharingWindow.Header title="Request A Review" />

        <AnokyeSharingWindow.Content>
          <AnokyeSharingWindow.ResourceDetails>
            <AnokyeSharingWindow.ResourceInfo>
              <BsChatLeftText />
              <p>{documentName}</p>
            </AnokyeSharingWindow.ResourceInfo>
          </AnokyeSharingWindow.ResourceDetails>

          <AnokyeSharingWindow.LinkSharing />
          <AnokyeSharingWindow.SearchMembers />
          <AnokyeSharingWindow.SelectedReviewers />
          <AnokyeSharingWindow.ReviewersInfo />
        </AnokyeSharingWindow.Content>
        <AnokyeSharingWindow.ReviewFooter />
      </AnokyeSharingWindow>
    );
  }
);

/**
 * @template T
 * @typedef {{openWindow(resource: T): void, closeWindow(): void }} SharingWindowExternalAPI
 */

/**
 * @template T
 * @param {React.ForwardedRef<SharingWindowExternalAPI<T>>} ref
 */
function useSharingWindowResourceAndRef(ref) {
  /** @type {ReactState<T | null>} */
  const [resource, setResource] = useState(null);
  /** @type {React.RefObject<import("./AnokyeSharingWindow").AnokyeSharingWindowComponent>} */
  const windowRef = useRef(null);

  useImperativeHandle(ref, () => ({
    openWindow,
    closeWindow,
  }));

  function openWindow(resource) {
    setResource(resource);
    windowRef.current?.openWindow();
  }

  function closeWindow() {
    windowRef.current?.closeWindow();
  }

  function handleWindowClose() {
    setResource(null);
  }

  return { resource, windowRef, handleWindowClose };
}
